import { useEffect, useState, useMemo, useRef } from "react";
import api, { fmtPKR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  UserPlus,
  Printer,
  X,
  ScanLine,
  FileDown,
  Share2,
  CheckCircle2,
  Trash2,
  PlusCircle,
} from "lucide-react";
import { toast } from "sonner";
import { downloadInvoicePdf, openWhatsAppShare, openInvoicePrint } from "../lib/invoice";
import PageShell from "../components/PageShell";

export default function POS() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [cart, setCart] = useState([]);
  const [parties, setParties] = useState([]);
  const [settings, setSettings] = useState(null);

  const [customerType, setCustomerType] = useState("walkin");
  const [selectedParty, setSelectedParty] = useState("");
  const [partySearch, setPartySearch] = useState("");
  const [showPartyList, setShowPartyList] = useState(false);
  const [walkinName, setWalkinName] = useState("Walk-in Customer");
  const [walkinPhone, setWalkinPhone] = useState("");

  const [overallDiscount, setOverallDiscount] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [notes, setNotes] = useState("");

  const [newPartyOpen, setNewPartyOpen] = useState(false);
  const [newParty, setNewParty] = useState({ name: "", phone: "", address: "", opening_balance: 0 });

  const [lastSale, setLastSale] = useState(null);
  const [postSaleOpen, setPostSaleOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: "",
    quantity: 1,
    purchase_price: 0,
    rate: 0,
    vendor_party_id: "",
    vendor_name: "",
  });
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const completeSaleRef = useRef(null);

  const addManualItem = () => {
    if (!manualForm.name.trim()) return toast.error("Enter a name");
    if ((Number(manualForm.rate) || 0) <= 0) return toast.error("Enter a sale price");
    setCart((c) => [
      ...c,
      {
        product_id: null,
        product_code: "MANUAL",
        name: manualForm.name.trim(),
        brand: "External",
        quantity: Number(manualForm.quantity) || 1,
        rate: Number(manualForm.rate) || 0,
        discount: 0,
        max_stock: Infinity,  // no stock check on manual items
        purchase_price: Number(manualForm.purchase_price) || 0,
        vendor_party_id: manualForm.vendor_party_id || null,
        vendor_name: manualForm.vendor_name || "",
      },
    ]);
    toast.success(`Added: ${manualForm.name.trim()}`);
    setManualForm({ name: "", quantity: 1, purchase_price: 0, rate: 0, vendor_party_id: "", vendor_name: "" });
    setVendorSearch("");
    setVendorDropdownOpen(false);
    setManualOpen(false);
  };

  const loadProducts = async (q = "") => {
    const res = await api.get("/products", { params: { search: q || undefined } });
    setProducts(res.data);
    return res.data;
  };

  const loadParties = async () => {
    const res = await api.get("/parties", { params: { type: "customer" } });
    setParties(res.data);
  };

  const loadSettings = async () => {
    const res = await api.get("/settings");
    setSettings(res.data);
  };

  useEffect(() => {
    loadProducts();
    loadParties();
    loadSettings();
    searchRef.current?.focus();
  }, []);

  // Keyboard shortcuts: "/" focus search, F9 complete sale, Delete removes last line
  useEffect(() => {
    const isTypingIn = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el.isContentEditable
      );
    };
    const onKey = (e) => {
      // "/" — focus search (only when not typing elsewhere)
      if (e.key === "/" && !isTypingIn(document.activeElement)) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      // F9 — complete sale
      if (e.key === "F9") {
        e.preventDefault();
        completeSaleRef.current?.();
        return;
      }
      // Delete — remove last cart line (only when not typing in a text field)
      if (e.key === "Delete" && !isTypingIn(document.activeElement)) {
        e.preventDefault();
        setCart((c) => c.slice(0, -1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadProducts(search), 200);
    return () => clearTimeout(t);
  }, [search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !searchRef.current?.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.trim().toLowerCase();
    return products.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.product_code || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q) ||
        (p.barcode || "").toLowerCase().includes(q)
    );
  }, [products, search]);

  const addToCart = (p) => {
    // Block completely if the product has zero stock
    if ((p.current_stock || 0) <= 0) {
      toast.error(`${p.name} is out of stock`);
      return;
    }
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id);
      if (ex) {
        // Do not exceed available stock on increment
        if ((Number(ex.quantity) || 0) + 1 > (p.current_stock || 0)) {
          toast.error(`Only ${p.current_stock} in stock`);
          return c;
        }
        return c.map((x) =>
          x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x
        );
      }
      return [
        ...c,
        {
          product_id: p.id,
          product_code: p.product_code,
          name: p.name,
          brand: p.brand,
          quantity: 1,
          rate: p.sale_price,
          discount: 0,
          max_stock: p.current_stock,
        },
      ];
    });
    setSearch("");
    setShowDropdown(false);
    searchRef.current?.focus();
  };

  const handleSearchKey = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const q = search.trim();
      if (!q) return;
      // Try exact match (barcode/code) first — for scanners
      let match =
        products.find((p) => (p.barcode || "").toLowerCase() === q.toLowerCase()) ||
        products.find((p) => (p.product_code || "").toLowerCase() === q.toLowerCase());
      if (!match && filtered.length === 1) match = filtered[0];
      if (!match) {
        const data = await loadProducts(q);
        match =
          data.find((p) => (p.barcode || "").toLowerCase() === q.toLowerCase()) ||
          data.find((p) => (p.product_code || "").toLowerCase() === q.toLowerCase());
      }
      if (match) {
        addToCart(match);
        toast.success(`Added: ${match.name}`);
      } else {
        toast.error("No matching product");
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setSearch("");
    }
  };

  const updateCartItem = (idx, key, val) => {
    setCart((c) => c.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };
  const removeItem = (idx) => setCart((c) => c.filter((_, i) => i !== idx));

  const subtotal = useMemo(
    () =>
      cart.reduce(
        (s, it) =>
          s + (Number(it.quantity) || 0) * (Number(it.rate) || 0) - (Number(it.discount) || 0),
        0
      ),
    [cart]
  );
  const grandTotal = Math.max(0, subtotal - (Number(overallDiscount) || 0));
  const totalQty = cart.reduce((s, it) => s + (Number(it.quantity) || 0), 0);

  // Stock validation (skip manual items — they have Infinity max_stock)
  const insufficientLines = useMemo(
    () =>
      cart
        .map((it, i) => ({ ...it, i }))
        .filter(
          (it) =>
            typeof it.max_stock === "number" &&
            isFinite(it.max_stock) &&
            (Number(it.quantity) || 0) > it.max_stock
        ),
    [cart]
  );
  const hasInsufficient = insufficientLines.length > 0;

  const resetSale = () => {
    setCart([]);
    setOverallDiscount(0);
    setPayment("cash");
    setNotes("");
    setCustomerType("walkin");
    setSelectedParty("");
    setPartySearch("");
    setShowPartyList(false);
    setWalkinName("Walk-in Customer");
    setWalkinPhone("");
  };

  const completeSale = async () => {
    if (cart.length === 0) return toast.error("Add items to cart first");
    if (hasInsufficient) {
      const first = insufficientLines[0];
      return toast.error(
        `Insufficient stock: ${first.name} — only ${first.max_stock} available`
      );
    }
    if (customerType === "existing" && !selectedParty) return toast.error("Select a customer");
    if (payment === "ledger" && customerType !== "existing")
      return toast.error("Ledger payment requires an existing customer");
    try {
      const payload = {
        customer_type: customerType,
        party_id: customerType === "existing" ? selectedParty : null,
        customer_name:
          customerType === "existing"
            ? parties.find((p) => p.id === selectedParty)?.name || "Customer"
            : walkinName || "Walk-in Customer",
        items: cart.map((it) => ({
          product_id: it.product_id,
          product_code: it.product_code,
          name: it.name,
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
          discount: Number(it.discount) || 0,
          amount:
            (Number(it.quantity) || 0) * (Number(it.rate) || 0) - (Number(it.discount) || 0),
          purchase_price: Number(it.purchase_price) || 0,
          vendor_party_id: it.vendor_party_id || null,
          vendor_name: it.vendor_name || "",
        })),
        overall_discount: Number(overallDiscount) || 0,
        payment_method: payment,
        notes,
      };
      const res = await api.post("/sales", payload);
      setLastSale({
        ...res.data,
        customer_phone:
          customerType === "existing"
            ? parties.find((p) => p.id === selectedParty)?.phone || ""
            : walkinPhone,
      });
      toast.success(`Sale ${res.data.invoice_no} saved`);
      setPostSaleOpen(true);
      setTimeout(() => openInvoicePrint(res.data.id), 300);
      resetSale();
      loadProducts();
      searchRef.current?.focus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save sale");
    }
  };
  completeSaleRef.current = completeSale;

  const createParty = async () => {
    if (!newParty.name) return toast.error("Name required");
    try {
      const res = await api.post("/parties", { ...newParty, type: "customer" });
      toast.success("Customer added");
      await loadParties();
      setSelectedParty(res.data.id);
      setPartySearch(res.data.name + (res.data.phone ? ` · ${res.data.phone}` : ""));
      setShowPartyList(false);
      setCustomerType("existing");
      setNewPartyOpen(false);
      setNewParty({ name: "", phone: "", address: "", opening_balance: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <PageShell
        maxWidth="100%"
        ticker={[
          { label: "Console", value: "POINT OF SALE", live: true },
          { label: "Cart", value: String(cart.length).padStart(2, "0"), mono: true },
          { label: "Qty", value: String(totalQty).padStart(3, "0"), mono: true },
          { label: "Total", value: fmtPKR(grandTotal), mono: true },
        ]}
        eyebrow="Chapter · Register"
        title={
          <>
            Ring it <em className="italic text-blue-700">up</em>
          </>
        }
        description="Search or scan a part, add it to the cart, then complete the sale — one landscape view for the whole ceremony."
        colophon="POS"
      >
        <div className="pb-6" data-testid="pos-page">
          {/* ================= SEARCH BAR ================= */}
          <div className="mb-4 flex gap-2" data-testid="pos-search-wrapper">
            <div className="relative flex-1">
              <ScanLine
                className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 z-10"
                size={20}
              />
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShowDropdown(Boolean(e.target.value));
                }}
                onKeyDown={handleSearchKey}
                placeholder="Scan a barcode or search by name, code, brand…  (Enter to add exact match)"
                data-testid="pos-search-input"
                className="pl-12 h-14 text-base border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600 shadow-sm"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.24em] text-slate-400 font-mono-tabular flex items-center gap-3">
                <span className="hidden md:inline">
                  <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">/</kbd>{" "}
                  focus ·{" "}
                  <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">F9</kbd>{" "}
                  bill ·{" "}
                  <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">DEL</kbd>{" "}
                  pop
                </span>
                <span>{products.length} SKU · ↵ TO ADD</span>
              </div>
            </div>
            <Button
              onClick={() => setManualOpen(true)}
              variant="outline"
              className="h-14 px-4 border-slate-300 whitespace-nowrap"
              data-testid="pos-manual-btn"
              title="Add a one-off item not in inventory (e.g. bought from another shop)"
            >
              <PlusCircle size={16} className="mr-2" /> Manual item
            </Button>
          </div>

          {/* Inline search results — flow below the bar, never overlaps */}
          {showDropdown && search.trim() && (
            filtered.length === 0 ? (
              <div
                className="mb-4 px-4 py-3 text-sm text-slate-500 border-l-2 border-slate-300 bg-slate-50/60 rounded-sm"
                data-testid="pos-no-results"
              >
                No parts match <span className="font-mono-tabular text-slate-700">"{search}"</span> — press <kbd className="px-1 py-0.5 rounded border border-slate-200 bg-white text-[10px] text-slate-600">↵</kbd> to add a manual item, or refine your query.
              </div>
            ) : (
              <div
                ref={dropdownRef}
                className="mb-4 card-premium overflow-hidden max-h-[360px] overflow-y-auto"
                data-testid="pos-search-dropdown"
              >
                <div className="divide-y divide-slate-100">
                  <div className="px-5 py-2 flex items-center justify-between bg-slate-50/60 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-mono-tabular sticky top-0 border-b border-slate-100">
                    <span>{filtered.length} matches</span>
                    <span>Code · Name · Stock · Price</span>
                  </div>
                  {filtered.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => addToCart(p)}
                      data-testid={`product-row-${p.product_code}`}
                      className={`w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-blue-50 transition-colors ${
                        p.current_stock <= p.minimum_stock ? "bg-red-50/40" : ""
                      }`}
                    >
                      <span className="font-mono-tabular text-[11px] text-slate-500 w-20 shrink-0">
                        {p.product_code}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{p.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {p.brand || "—"}
                          {p.barcode ? ` · ${p.barcode}` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div
                          className={`text-xs font-mono-tabular ${
                            p.current_stock <= p.minimum_stock
                              ? "text-red-700 font-semibold"
                              : "text-slate-600"
                          }`}
                        >
                          {p.current_stock} in stock
                        </div>
                        <div className="text-sm tabular text-slate-900 font-medium">
                          {fmtPKR(p.sale_price)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )
          )}

          {/* ================= CART (landscape wide table) ================= */}
          <Card className="card-premium overflow-hidden mb-4">
            <div className="px-6 py-3 flex items-center justify-between border-b border-slate-100 bg-slate-50/60">
              <div className="flex items-center gap-3">
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                  Cart
                </div>
                <span className="font-mono-tabular text-xs text-slate-400">
                  {cart.length.toString().padStart(2, "0")} lines · {totalQty.toString().padStart(3, "0")} units
                </span>
              </div>
              {cart.length > 0 && (
                <button
                  onClick={() => setCart([])}
                  className="text-[11px] uppercase tracking-[0.24em] text-slate-500 hover:text-red-700 inline-flex items-center gap-1"
                  data-testid="cart-clear-all"
                >
                  <Trash2 size={12} /> Clear all
                </button>
              )}
            </div>

            {cart.length === 0 ? (
              <div className="py-24 text-center">
                <div className="font-serif italic text-slate-400 text-lg">
                  Cart is empty
                </div>
                <div className="text-xs text-slate-400 mt-2">
                  Use the search bar above to add parts.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.24em] text-slate-500 border-b border-slate-100">
                    <th className="px-4 py-3 text-left font-medium w-12">#</th>
                    <th className="px-4 py-3 text-left font-medium">Part</th>
                    <th className="px-4 py-3 text-right font-medium w-28">Qty</th>
                    <th className="px-4 py-3 text-right font-medium w-36">Rate (₨)</th>
                    <th className="px-4 py-3 text-right font-medium w-32">Discount (₨)</th>
                    <th className="px-4 py-3 text-right font-medium w-36">Amount</th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((it, idx) => {
                    const qtyN = Number(it.quantity) || 0;
                    const amt = qtyN * (Number(it.rate) || 0) - (Number(it.discount) || 0);
                    const insufficient =
                      typeof it.max_stock === "number" && qtyN > it.max_stock;
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-slate-100 hover:bg-slate-50/60 ${
                          insufficient ? "bg-red-50/70 hover:bg-red-100/60" : ""
                        }`}
                        data-testid={`cart-item-${idx}`}
                      >
                        <td className="px-4 py-3 font-mono-tabular text-[11px] text-slate-400">
                          {String(idx + 1).padStart(2, "0")}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-900 flex items-center gap-2">
                            {it.name}
                            {it.product_code === "MANUAL" && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.16em] bg-blue-50 text-blue-700 border border-blue-200">
                                Manual
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono-tabular flex items-center gap-2">
                            <span>
                              {it.product_code}
                              {it.brand ? ` · ${it.brand}` : ""}
                            </span>
                            {insufficient && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.16em] bg-red-100 text-red-700 border border-red-200"
                                data-testid={`cart-insufficient-${idx}`}
                              >
                                ⚠ Only {it.max_stock} in stock
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="0"
                            value={it.quantity}
                            onChange={(e) => updateCartItem(idx, "quantity", e.target.value)}
                            className={`h-9 text-right tabular ${
                              insufficient ? "border-red-300 text-red-700 font-semibold" : ""
                            }`}
                            data-testid={`cart-qty-${idx}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="0"
                            value={it.rate}
                            onChange={(e) => updateCartItem(idx, "rate", e.target.value)}
                            className="h-9 text-right tabular"
                            data-testid={`cart-rate-${idx}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="0"
                            value={it.discount}
                            onChange={(e) => updateCartItem(idx, "discount", e.target.value)}
                            className="h-9 text-right tabular"
                            data-testid={`cart-disc-${idx}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="tabular font-semibold text-slate-900">
                            {fmtPKR(amt)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => removeItem(idx)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`cart-remove-${idx}`}
                          >
                            <X size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </Card>

          {/* ================= FOOTER — landscape row ================= */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Customer */}
            <Card className="card-premium p-5 lg:col-span-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                  Customer
                </div>
                <button
                  onClick={() => setNewPartyOpen(true)}
                  data-testid="pos-add-customer-btn"
                  className="text-[11px] text-blue-700 inline-flex items-center gap-1 hover:underline"
                >
                  <UserPlus size={12} /> New
                </button>
              </div>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setCustomerType("walkin")}
                  data-testid="pos-walkin-btn"
                  className={`flex-1 px-3 py-2 text-xs rounded border ${
                    customerType === "walkin"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  Walk-in
                </button>
                <button
                  onClick={() => setCustomerType("existing")}
                  data-testid="pos-existing-btn"
                  className={`flex-1 px-3 py-2 text-xs rounded border ${
                    customerType === "existing"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  Existing
                </button>
              </div>

              {customerType === "walkin" ? (
                <div className="space-y-2">
                  <Input
                    value={walkinName}
                    onChange={(e) => setWalkinName(e.target.value)}
                    placeholder="Customer name (optional)"
                    data-testid="pos-walkin-name-input"
                  />
                  <Input
                    value={walkinPhone}
                    onChange={(e) => setWalkinPhone(e.target.value)}
                    placeholder="WhatsApp phone (optional)"
                    data-testid="pos-walkin-phone-input"
                  />
                </div>
              ) : (
                <div className="relative">
                  <Input
                    value={partySearch}
                    onChange={(e) => {
                      setPartySearch(e.target.value);
                      if (selectedParty) setSelectedParty("");
                    }}
                    onFocus={() => setShowPartyList(true)}
                    placeholder="Search customer by name or phone…"
                    data-testid="pos-party-search"
                  />
                  {showPartyList && (
                    <div
                      className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-md max-h-56 overflow-y-auto shadow-md"
                      data-testid="pos-party-results"
                    >
                      {parties
                        .filter(
                          (p) =>
                            (p.type === "customer" || p.type === "both") &&
                            (
                              !partySearch.trim() ||
                              (p.name || "").toLowerCase().includes(partySearch.toLowerCase()) ||
                              (p.phone || "").toLowerCase().includes(partySearch.toLowerCase())
                            )
                        )
                        .slice(0, 25)
                        .map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => {
                              setSelectedParty(p.id);
                              setPartySearch(p.name + (p.phone ? ` · ${p.phone}` : ""));
                              setShowPartyList(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-b-0 ${
                              selectedParty === p.id ? "bg-blue-50" : ""
                            }`}
                            data-testid={`pos-party-opt-${p.id}`}
                          >
                            <div className="text-slate-900">{p.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              {p.type}{p.phone ? ` · ${p.phone}` : ""}
                            </div>
                          </button>
                        ))}
                      {parties.filter(
                        (p) =>
                          (p.type === "customer" || p.type === "both") &&
                          (
                            !partySearch.trim() ||
                            (p.name || "").toLowerCase().includes(partySearch.toLowerCase()) ||
                            (p.phone || "").toLowerCase().includes(partySearch.toLowerCase())
                          )
                      ).length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400">
                          No customers match. Use "New" to add one.
                        </div>
                      )}
                    </div>
                  )}
                  {selectedParty && !showPartyList && (
                    <div className="mt-1 text-[11px] text-emerald-700">
                      Selected ✓{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedParty("");
                          setPartySearch("");
                          setShowPartyList(true);
                        }}
                        className="text-slate-500 underline"
                        data-testid="pos-party-change"
                      >
                        change
                      </button>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Payment */}
            <Card className="card-premium p-5 lg:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-3">
                Payment method
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => setPayment("cash")}
                  data-testid="pos-pay-cash"
                  className={`px-3 py-3 text-sm rounded border ${
                    payment === "cash"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  Cash
                </button>
                <button
                  onClick={() => setPayment("ledger")}
                  data-testid="pos-pay-ledger"
                  className={`px-3 py-3 text-sm rounded border ${
                    payment === "ledger"
                      ? "bg-blue-700 text-white border-blue-700"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  Ledger
                </button>
              </div>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="text-xs"
                data-testid="pos-notes"
              />
            </Card>

            {/* Totals + CTA */}
            <Card
              className="p-5 lg:col-span-5 relative overflow-hidden text-white"
              style={{
                background: "linear-gradient(135deg, #05070d 0%, #0b1220 60%, #0a1a3d 100%)",
                boxShadow: "0 12px 32px -12px rgba(5,7,13,0.4)",
              }}
            >
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-1">
                    Subtotal
                  </div>
                  <div className="tabular text-lg text-slate-100 font-medium">
                    {fmtPKR(subtotal)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-1">
                    Overall Discount (₨)
                  </div>
                  <Input
                    type="number"
                    min="0"
                    value={overallDiscount}
                    onChange={(e) => setOverallDiscount(e.target.value)}
                    data-testid="pos-overall-discount"
                    className="h-9 tabular bg-white/10 border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              <div className="flex items-end justify-between pt-3 border-t border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
                    Grand total
                  </div>
                  <div
                    className="big-number text-white text-[42px] leading-none mt-1"
                    data-testid="pos-grand-total"
                  >
                    {fmtPKR(grandTotal)}
                  </div>
                  {hasInsufficient && (
                    <div
                      className="text-[10px] uppercase tracking-[0.24em] text-red-300 mt-2 inline-flex items-center gap-1"
                      data-testid="pos-block-reason"
                    >
                      ⚠ Cannot bill — {insufficientLines.length} line
                      {insufficientLines.length > 1 ? "s" : ""} exceed stock
                    </div>
                  )}
                </div>
                <Button
                  onClick={completeSale}
                  disabled={cart.length === 0 || hasInsufficient}
                  data-testid="pos-complete-sale-btn"
                  className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white h-14 px-6 text-base"
                  style={{
                    boxShadow: hasInsufficient
                      ? "none"
                      : "0 8px 24px -8px rgba(37,99,235,0.6)",
                  }}
                  title={
                    hasInsufficient
                      ? "Reduce quantities on red rows first"
                      : "Complete Sale (F9)"
                  }
                >
                  <Printer size={18} className="mr-2" />
                  Complete &amp; Print
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </PageShell>

      {/* Add party dialog */}
      <Dialog open={newPartyOpen} onOpenChange={setNewPartyOpen}>
        <DialogContent data-testid="new-party-dialog">
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                value={newParty.name}
                onChange={(e) => setNewParty({ ...newParty, name: e.target.value })}
                data-testid="new-party-name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={newParty.phone}
                onChange={(e) => setNewParty({ ...newParty, phone: e.target.value })}
                data-testid="new-party-phone"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={newParty.address}
                onChange={(e) => setNewParty({ ...newParty, address: e.target.value })}
                data-testid="new-party-address"
              />
            </div>
            <div>
              <Label>Opening Balance (₨)</Label>
              <Input
                type="number"
                value={newParty.opening_balance}
                onChange={(e) =>
                  setNewParty({ ...newParty, opening_balance: Number(e.target.value) })
                }
                data-testid="new-party-balance"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPartyOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createParty}
              data-testid="new-party-save"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              Save Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual product entry dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle size={18} className="text-blue-700" />
              Add Manual Item
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            For one-off items not in your inventory (e.g. a part fetched from another shop).
            If you pick a vendor and set a purchase price, a purchase ledger entry is created
            automatically.
          </p>
          <div className="space-y-3">
            <div>
              <Label>Item name *</Label>
              <Input
                value={manualForm.name}
                onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                placeholder="e.g. Brake Pad from Rehmani Store"
                autoFocus
                data-testid="manual-name-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Quantity *</Label>
                <Input
                  id="mfq"
                  type="number"
                  min="1"
                  value={manualForm.quantity}
                  onChange={(e) => setManualForm({ ...manualForm, quantity: e.target.value })}
                  data-testid="manual-qty-input"
                />
              </div>
              <div>
                <Label>Purchase Price (₨)</Label>
                <Input
                  type="number"
                  min="0"
                  value={manualForm.purchase_price}
                  onChange={(e) => setManualForm({ ...manualForm, purchase_price: e.target.value })}
                  placeholder="Cost from vendor"
                  data-testid="manual-purchase-input"
                />
              </div>
              <div>
                <Label>Sale Price (₨) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={manualForm.rate}
                  onChange={(e) => setManualForm({ ...manualForm, rate: e.target.value })}
                  data-testid="manual-price-input"
                />
              </div>
            </div>
            <div>
              <Label>Vendor / Party (optional)</Label>
              <Input
                value={vendorSearch}
                onChange={(e) => {
                  setVendorSearch(e.target.value);
                  // Clear selection if user types freely
                  if (manualForm.vendor_name && e.target.value !== manualForm.vendor_name) {
                    setManualForm({ ...manualForm, vendor_party_id: "", vendor_name: "" });
                  }
                }}
                onFocus={() => setVendorDropdownOpen(true)}
                placeholder="Click to see all parties or type to search…"
                data-testid="manual-vendor-search"
              />
              {vendorDropdownOpen && !manualForm.vendor_party_id && (
                <div
                  className="mt-1 border border-slate-200 rounded-md max-h-56 overflow-y-auto bg-white shadow-sm"
                  data-testid="manual-vendor-results"
                >
                  {(() => {
                    const q = vendorSearch.trim().toLowerCase();
                    const filtered = parties.filter((p) =>
                      !q ||
                      (p.name || "").toLowerCase().includes(q) ||
                      (p.phone || "").toLowerCase().includes(q)
                    );
                    if (filtered.length === 0) {
                      return (
                        <div className="px-3 py-2 text-xs text-slate-400">
                          No parties yet. Add vendors from Party Ledger.
                        </div>
                      );
                    }
                    return filtered.slice(0, 50).map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setManualForm({ ...manualForm, vendor_party_id: v.id, vendor_name: v.name });
                          setVendorSearch(v.name);
                          setVendorDropdownOpen(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                        data-testid={`manual-vendor-opt-${v.id}`}
                      >
                        <div className="text-slate-900">{v.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                          {v.type}{v.phone ? ` · ${v.phone}` : ""}
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              )}
              {manualForm.vendor_party_id && (
                <div className="mt-1 text-[11px] text-emerald-700 flex items-center gap-2">
                  Vendor selected:{" "}
                  <span className="font-medium">{manualForm.vendor_name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setManualForm({ ...manualForm, vendor_party_id: "", vendor_name: "" });
                      setVendorSearch("");
                      setVendorDropdownOpen(true);
                    }}
                    className="text-slate-400 hover:text-red-700"
                    data-testid="manual-vendor-clear"
                  >
                    clear
                  </button>
                </div>
              )}
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600 space-y-1">
              <div className="flex items-center justify-between">
                <span>Sale line total (customer pays)</span>
                <span className="tabular font-semibold text-slate-900">
                  {fmtPKR((Number(manualForm.quantity) || 0) * (Number(manualForm.rate) || 0))}
                </span>
              </div>
              {Number(manualForm.purchase_price) > 0 && (
                <div className="flex items-center justify-between text-slate-500">
                  <span>Purchase cost (vendor ledger)</span>
                  <span className="tabular">
                    {fmtPKR(
                      (Number(manualForm.quantity) || 0) *
                        (Number(manualForm.purchase_price) || 0)
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button
              onClick={addManualItem}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="manual-save-btn"
            >
              <PlusCircle size={14} className="mr-2" /> Add to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-sale dialog — larger, landscape */}
      <Dialog open={postSaleOpen} onOpenChange={setPostSaleOpen}>
        <DialogContent
          data-testid="post-sale-dialog"
          className="max-w-3xl p-0 overflow-hidden"
        >
          <div
            className="p-8 text-white relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #05070d 0%, #0b1220 60%, #0a1a3d 100%)",
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-400/30">
                <CheckCircle2 className="text-emerald-400" size={20} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
                  Sale complete · Real-time
                </div>
                <div className="font-serif text-2xl text-white leading-tight">
                  Rung up successfully.
                </div>
              </div>
            </div>

            {lastSale && (
              <div className="grid grid-cols-3 gap-6 pb-6 border-b border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-1">
                    Invoice
                  </div>
                  <div className="font-mono-tabular text-lg text-white">
                    {lastSale.invoice_no}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-1">
                    Customer
                  </div>
                  <div className="text-lg text-white truncate">{lastSale.customer_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-1">
                    Grand Total
                  </div>
                  <div className="big-number text-blue-300 text-2xl leading-none mt-1">
                    {fmtPKR(lastSale.grand_total)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {lastSale && (
            <div className="p-6 bg-white">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-3">
                Send the invoice
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Button
                  variant="outline"
                  onClick={() => openInvoicePrint(lastSale.id)}
                  data-testid="post-sale-print-btn"
                  className="flex-col h-auto py-5 gap-2"
                >
                  <Printer size={22} strokeWidth={1.6} />
                  <span className="text-xs">Print A4</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    downloadInvoicePdf(lastSale.id, lastSale.invoice_no)
                  }
                  data-testid="post-sale-pdf-btn"
                  className="flex-col h-auto py-5 gap-2"
                >
                  <FileDown size={22} strokeWidth={1.6} />
                  <span className="text-xs">Download PDF</span>
                </Button>
                <Button
                  onClick={() =>
                    openWhatsAppShare(lastSale, settings, lastSale.customer_phone)
                  }
                  data-testid="post-sale-whatsapp-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 flex-col h-auto py-5 gap-2 text-white"
                >
                  <Share2 size={22} strokeWidth={1.8} />
                  <span className="text-xs">WhatsApp</span>
                </Button>
              </div>
              <div className="pt-4 flex justify-end">
                <Button
                  onClick={() => setPostSaleOpen(false)}
                  variant="ghost"
                  data-testid="post-sale-close-btn"
                  className="text-slate-500"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
